import { type OperationVariables } from '@apollo/react-common';
import { type DataProxy } from 'apollo-cache';
import { type GraphQLTaggedNode } from 'react-relay';
import {
	type ConcreteRequest,
	type ReaderArgument,
	type ReaderLinkedField,
	type ReaderScalarField,
	type ReaderSelection,
	ROOT_ID,
} from 'relay-runtime';

import { isRelayEnabled } from './isRelayEnabled.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import {
	readQueryWithCacheBridgeFallback,
	type QueryCacheBridgeSource,
} from './cacheBridge/queryCacheBridgeSync.ts';

export type ReadQueryInteropArgs<TVariables extends Record<string, any> = OperationVariables> =
	Pick<DataProxy.Query<TVariables>, 'variables'> & {
		query: GraphQLTaggedNode;
		/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
		__callerPackage__?: string;
	};

export function readQueryInterop<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(params: ReadQueryInteropArgs<TVariables>): TData | null {
	const source: QueryCacheBridgeSource = isRelayEnabled(params.__callerPackage__)
		? 'relay'
		: 'apollo';

	return readQueryWithCacheBridgeFallback<TData, TVariables>({
		source,
		query: params.query,
		variables: params.variables,
		readOriginal: () => readQueryInteropOriginal<TData, TVariables>(params, source),
	});
}

function readQueryInteropOriginal<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(params: ReadQueryInteropArgs<TVariables>, source: QueryCacheBridgeSource): TData | null {
	return source === 'relay'
		? readQueryInteropRelay<TData, TVariables>(params)
		: readQueryInteropApollo<TData, TVariables>(params);
}

function readQueryInteropRelay<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: ReadQueryInteropArgs<TVariables>,
): TData | null {
	// Guard window for SSR; this only runs client-side.
	const relayEnv = typeof window !== 'undefined' ? window.GLOBAL_RELAY_ENV : undefined;
	if (!relayEnv) {
		return null;
	}

	const source = relayEnv.getStore().getSource();
	const rootRecord = source.get(ROOT_ID);
	if (!rootRecord) {
		return null;
	}

	const queryNode = params.query as ConcreteRequest;
	const queryFields = queryNode?.operation?.selections;
	if (!queryFields) {
		return null;
	}

	const variables: Record<string, any> = (params.variables as Record<string, any>) ?? {};

	return readFields(
		source,
		ROOT_ID,
		queryFields as readonly ReaderSelection[],
		variables,
	) as TData | null;
}

function resolveArgValue(arg: ReaderArgument, variables: Record<string, any>): unknown {
	if (arg.kind === 'Literal') {
		return arg.value;
	}
	if (arg.kind === 'Variable') {
		return variables[arg.variableName];
	}
	// Fallback for other argument types
	return (arg as any).value;
}

function getFieldKey(
	field: ReaderLinkedField | ReaderScalarField,
	variables: Record<string, any>,
): string {
	// Relay pre-computes `storageKey` for literal-only args; prefer it when present.
	if (field.storageKey) {
		return field.storageKey;
	}
	if (!field.args || field.args.length === 0) {
		return field.name;
	}

	// Mirror Relay's `formatStorageKey` (relay-runtime/store/RelayStoreUtils): sort args
	// alphabetically and omit any with a null/undefined value. Skipping the omission would
	// reconstruct a key Relay never wrote (e.g. `teamType:null`), so the lookup misses and
	// the field reads back as `null`.
	const sortedArgs = field.args.slice().sort((a, b) => a.name.localeCompare(b.name));
	const formattedArgs: string[] = [];
	for (const arg of sortedArgs) {
		const value = resolveArgValue(arg, variables);
		if (value != null) {
			formattedArgs.push(`${arg.name}:${JSON.stringify(value)}`);
		}
	}

	return formattedArgs.length === 0 ? field.name : `${field.name}(${formattedArgs.join(',')})`;
}

type RecordSourceLike = {
	get: (dataId: string) => Record<string, any> | undefined | null;
};

/* eslint-disable @atlassian/perf-linting/no-quadratic-complexity */
function readFields(
	source: RecordSourceLike,
	recordId: string,
	fields: readonly ReaderSelection[],
	variables: Record<string, any>,
): Record<string, any> | null {
	const record = source.get(recordId);
	if (!record) {
		return null;
	}

	const data: Record<string, any> = {};

	for (const field of fields) {
		if (field.kind === 'Condition') {
			// Handle @include/@skip directives with variables
			const conditionValue = variables[field.condition];
			const shouldInclude = field.passingValue ? conditionValue : !conditionValue;

			if (shouldInclude) {
				const conditionalData = readFields(source, recordId, field.selections, variables);
				if (conditionalData) {
					Object.assign(data, conditionalData);
				}
			}
		} else if (field.kind === 'LinkedField') {
			const linkedField = field as ReaderLinkedField;
			const fieldKey = getFieldKey(linkedField, variables);
			const linkedRef = record[fieldKey];

			if (linkedField.plural) {
				// Relay stores plural refs in two shapes:
				//  - canonical: { __refs: ['id1', 'id2', ...] }
				//  - tolerated: [{ __ref: 'id1' }, { __ref: 'id2' }]
				if (
					linkedRef &&
					typeof linkedRef === 'object' &&
					!Array.isArray(linkedRef) &&
					Array.isArray((linkedRef as { __refs?: unknown }).__refs)
				) {
					const refs = (linkedRef as { __refs: Array<string | null> }).__refs;
					data[linkedField.name] = refs.map((id) =>
						id ? readFields(source, id, linkedField.selections, variables) : null,
					);
				} else if (Array.isArray(linkedRef)) {
					data[linkedField.name] = linkedRef.map((ref) => {
						if (ref && typeof ref === 'object' && '__ref' in ref) {
							return readFields(
								source,
								(ref as { __ref: string }).__ref,
								linkedField.selections,
								variables,
							);
						}
						return null;
					});
				} else {
					data[linkedField.name] = null;
				}
			} else if (linkedRef && typeof linkedRef === 'object' && '__ref' in linkedRef) {
				data[linkedField.name] = readFields(
					source,
					(linkedRef as { __ref: string }).__ref,
					linkedField.selections,
					variables,
				);
			} else {
				data[linkedField.name] = null;
			}
		} else if (field.kind === 'ScalarField') {
			const scalarField = field as ReaderScalarField;
			const fieldKey = getFieldKey(scalarField, variables);
			data[scalarField.name] = record[fieldKey];
		} else if (field.kind === 'InlineFragment') {
			// Process all inline fragment selections
			const inlineData = readFields(source, recordId, field.selections, variables);
			if (inlineData) {
				Object.assign(data, inlineData);
			}
		}
		// Other selection kinds (e.g., FragmentSpread, ClientExtension) are ignored —
		// readQuery only needs to traverse what the query operation directly selects.
	}

	return data;
}

/* eslint-enable @atlassian/perf-linting/no-quadratic-complexity */
function readQueryInteropApollo<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: ReadQueryInteropArgs<TVariables>,
): TData | null {
	return window['GLOBAL_APOLLO_CLIENT']!.readQuery<TData, TVariables>({
		...params,
		query: toDocumentNode(params.query),
	});
}
